import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { getApiAppsettingsPrivacyPolicyOptions, getApiAppsettingsTermsAndConditionsOptions } from 'api/generated/@tanstack/react-query.gen';
import { useAppState } from 'lib/app-state';
export type LegalDocumentKind = 'termsAndConditions' | 'privacyPolicy';

/** The storefront's terms or privacy policy, shaped for the legal document dialog and page. */
export function useLegalDocument(document: LegalDocumentKind, enabled = true) {
  const intl = useIntl();
  const {
    locale
  } = useAppState();

  // The Culture header is part of the query key, so each language is cached on its own.
  const options = {
    headers: {
      Culture: locale
    }
  };
  const query = useQuery({
    ...(document === 'termsAndConditions' ? getApiAppsettingsTermsAndConditionsOptions(options) : getApiAppsettingsPrivacyPolicyOptions(options)),
    enabled,
    staleTime: Infinity,
    retry: (failureCount, error) => error.response?.status !== 404 && failureCount < 3
  });
  const notFound = query.error?.response?.status === 404;
  return {
    label: intl.formatMessage(document === 'termsAndConditions' ? {
      id: 'footer.termsAndConditions',
      defaultMessage: 'Terms & conditions'
    } : {
      id: 'footer.privacyPolicy',
      defaultMessage: 'Privacy policy'
    }),
    title: query.data?.title ?? undefined,
    text: query.data?.text ?? undefined,
    loading: query.isFetching && !query.data,
    error: query.isError,
    errorText: notFound ? intl.formatMessage({
      id: "legalDocument.notAvailable",
      defaultMessage: [{
        "type": 0,
        "value": "This document is not available."
      }]
    }) : intl.formatMessage({
      id: "legalDocument.loadError",
      defaultMessage: [{
        "type": 0,
        "value": "This document could not be loaded."
      }]
    }),
    retryText: intl.formatMessage({
      id: "common.tryAgain",
      defaultMessage: [{
        "type": 0,
        "value": "Try again"
      }]
    }),
    onRetry: notFound ? undefined : () => query.refetch()
  };
}